/**
 * El botón con link que cuenta quién lo toca (pedido de Jean Pierre Caipo, de Llama Leads, en la reunión del
 * 26/10/2026: «que se traqueen los clics»). El link que ve y aprueba Meta es el de Deenex, que termina en una variable
 * (`…/api/v1/activa/public/c/{{N}}`), y el link real queda en `destino`. Cada mensaje lleva su propio código en esa
 * variable: no se le pide a nadie, ni en la campaña ni al reabrir una conversación. Puras; la otra mitad es
 * `util/clic.util.ts` del backend.
 */

import type { BotonPlantilla, PlantillaBorrador, VariablePlantilla } from "../types";
import { siguienteNumero } from "./variables";

const URL_CLIC_RE = /\/api\/v1\/activa\/public\/c\/\{\{([1-9]\d{0,2})\}\}$/;
/** El valor de ejemplo de la variable, el que ve Meta al aprobar: la ruta del backend lo contesta sin anotar nada. */
export const EJEMPLO_CLIC = "ejemplo";

/** El número de la variable si el link es el de Deenex que cuenta clics; null si es un link común. */
export function numeroDeClic(url: string | null | undefined): number | null {
  const m = URL_CLIC_RE.exec(String(url ?? "").trim());
  return m ? Number(m[1]) : null;
}

/** Los números de las variables de los botones que cuentan clics. */
export function numerosDeClic(botones: Pick<BotonPlantilla, "tipo" | "url">[] | null | undefined): number[] {
  const out: number[] = [];
  for (const b of botones || []) {
    const n = b?.tipo === "URL" ? numeroDeClic(b.url) : null;
    if (n !== null && !out.includes(n)) out.push(n);
  }
  return out;
}

/** Las variables que sí se completan con un dato o un texto: todas menos las de los clics. */
export function variablesSinClics(variables: VariablePlantilla[] | null | undefined, botones: Pick<BotonPlantilla, "tipo" | "url">[] | null | undefined): VariablePlantilla[] {
  const deClic = numerosDeClic(botones);
  return (variables || []).filter((v) => !deClic.includes(v.numero));
}

/**
 * El botón `i` pasa a contar los clics: su link actual queda como destino, el link a Meta es `prefijo + {{N}}` (N, la
 * siguiente variable libre) y la variable se declara con su nombre y el ejemplo que necesita Meta.
 */
export function conClic(b: PlantillaBorrador, i: number, prefijo: string): PlantillaBorrador {
  const boton = b.botones?.[i];
  if (!boton || boton.tipo !== "URL" || numeroDeClic(boton.url) !== null) return b;
  const n = siguienteNumero(b);
  const variable: VariablePlantilla = { numero: n, alias: `Clic en «${boton.titulo.trim() || "el botón"}»`, ejemplo: EJEMPLO_CLIC };
  return {
    ...b,
    botones: b.botones.map((x, j) => (j === i ? { ...x, url: `${prefijo}{{${n}}}`, destino: (x.url || "").trim() } : x)),
    variables: [...(b.variables || []).filter((v) => v.numero !== n), variable].sort((x, y) => x.numero - y.numero),
  };
}

/** El botón `i` vuelve a ser un link común: el destino vuelve a ser su link y se saca la variable del clic. */
export function sinClic(b: PlantillaBorrador, i: number): PlantillaBorrador {
  const boton = b.botones?.[i];
  const n = numeroDeClic(boton?.url);
  if (!boton || n === null) return b;
  const { destino, ...resto } = boton;
  return {
    ...b,
    botones: b.botones.map((x, j) => (j === i ? { ...resto, url: destino || "" } : x)),
    variables: (b.variables || []).filter((v) => v.numero !== n),
  };
}
